import './election-notice.css';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api-client.js';

interface PublicAnnouncement {
  title: string;
  message: string;
  context: string;
  source_label: string;
  source_url: string;
  starts_at: string;
  ends_at: string;
  revision: string;
}

interface PublicAnnouncementResponse { announcement: PublicAnnouncement | null }

function dismissedKey(revision: string): string {
  return `vitrine:site-announcement:dismissed:${revision}`;
}

function wasDismissed(revision: string): boolean {
  try { return sessionStorage.getItem(dismissedKey(revision)) === 'dismissed'; }
  catch { return false; }
}

function markDismissed(revision: string): void {
  try { sessionStorage.setItem(dismissedKey(revision), 'dismissed'); }
  catch { /* O fechamento funciona mesmo quando o navegador bloqueia o armazenamento. */ }
}

function isActive(announcement: PublicAnnouncement, now = Date.now()): boolean {
  return Number.isFinite(Date.parse(announcement.starts_at))
    && Number.isFinite(Date.parse(announcement.ends_at))
    && now >= Date.parse(announcement.starts_at)
    && now < Date.parse(announcement.ends_at);
}

function formatEnd(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'long', timeStyle: 'short', timeZone: 'America/Sao_Paulo',
  }).format(new Date(value));
}

export function ElectionNotice() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openedRevision = useRef<string | null>(null);
  const bodyOverflow = useRef('');
  const [announcement, setAnnouncement] = useState<PublicAnnouncement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      try {
        const response = await api<PublicAnnouncementResponse>('/api/public/site-announcement', { cache: 'no-store' });
        if (disposed) return;
        const next = response.announcement;
        setAnnouncement(next && isActive(next) ? next : null);
        if (!next || !isActive(next)) { setOpen(false); return; }
        if (openedRevision.current === next.revision) return;
        openedRevision.current = next.revision;
        setOpen(!wasDismissed(next.revision));
      } catch {
        // Não exibe conteúdo antigo quando a configuração não pôde ser consultada.
      }
    };
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, 60_000);
    return () => { disposed = true; window.clearInterval(interval); };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && announcement && !dialog.open) {
      bodyOverflow.current = document.body.style.overflow;
      dialog.showModal();
      dialog.querySelector<HTMLButtonElement>('.election-notice-close')?.focus();
      document.body.style.overflow = 'hidden';
    } else if (!open && dialog.open) {
      dialog.close();
      document.body.style.overflow = bodyOverflow.current;
    }
  }, [open, announcement]);

  useEffect(() => {
    if (!announcement) return;
    const untilExpiry = Math.max(0, Date.parse(announcement.ends_at) - Date.now());
    const timer = window.setTimeout(() => { setOpen(false); setAnnouncement(null); }, untilExpiry);
    return () => window.clearTimeout(timer);
  }, [announcement]);

  useEffect(() => () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    document.body.style.overflow = bodyOverflow.current;
  }, []);

  const dismiss = () => {
    if (announcement) markDismissed(announcement.revision);
    setOpen(false);
  };

  return <>
    {announcement && !open && <button className="election-notice-reopen" type="button" aria-label="Reabrir aviso do site" title="Reabrir aviso" onClick={() => setOpen(true)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></svg>
    </button>}
    <dialog ref={dialogRef} className="election-notice" aria-labelledby="election-notice-title" aria-describedby="election-notice-description" onCancel={(event) => { event.preventDefault(); dismiss(); }} onClick={(event) => { if (event.target === event.currentTarget) dismiss(); }}>
      {announcement && <div className="election-notice-content">
        <div className="election-notice-topline">
          <span className="election-notice-label"><span aria-hidden="true" />Aviso do site</span>
          <button className="election-notice-close" type="button" aria-label="Fechar aviso" onClick={dismiss}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
        </div>
        <h2 id="election-notice-title">{announcement.title}</h2>
        <p className="election-notice-date">Válido até <time dateTime={announcement.ends_at}>{formatEnd(announcement.ends_at)}</time> · Horário de Brasília</p>
        <p id="election-notice-description">{announcement.message}</p>
        {announcement.context && <p className="election-notice-context">{announcement.context}</p>}
        <div className="election-notice-actions">
          {announcement.source_url && <a href={announcement.source_url} target="_blank" rel="noopener noreferrer">{announcement.source_label || 'Fonte'} <span aria-hidden="true">↗</span><span className="visually-hidden"> (abre em nova aba)</span></a>}
          <button className="election-notice-confirm" type="button" onClick={dismiss}>Entendi, continuar</button>
        </div>
      </div>}
    </dialog>
  </>;
}
